import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { generatePath, Link, useNavigate, useParams } from 'react-router'
import { InlineBanner } from '@/components/commons/inline-banner'
import { Button } from '@/components/ui/button'
import { PlusIcon, SearchLgIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { formatDate } from '@/dayjs'
import { AddAttendeeDialog } from '@/features/marquee/components/add-attendee-dialog'
import {
	AssignHostDialog,
	type AssignHostTarget
} from '@/features/marquee/components/assign-host-dialog'
import { AttendeeSummary } from '@/features/marquee/components/attendee-summary'
import { AttendeesTable } from '@/features/marquee/components/attendees-table'
import { DeleteEventDialog } from '@/features/marquee/components/delete-event-dialog'
import {
	handleImportError,
	showImportSuccessToast
} from '@/features/marquee/components/import-toasts'
import { IncompleteAccountsBanner } from '@/features/marquee/components/incomplete-accounts-banner'
import { LockEventDialog } from '@/features/marquee/components/lock-event-dialog'
import {
	MarkUnresolvableDialog,
	type UnresolvableTarget
} from '@/features/marquee/components/mark-unresolvable-dialog'
import { MarqueeTableCard } from '@/features/marquee/components/marquee-table-card'
import {
	ResponsiveConfirmDialog,
	ResponsiveConfirmTrigger
} from '@/features/marquee/components/responsive-confirm-dialog'
import { RosterFilterSelect } from '@/features/marquee/components/roster-filter-select'
import { SearchInput } from '@/features/marquee/components/search-input'
import { StartEventDialog } from '@/features/marquee/components/start-event-dialog'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { useMobileSearchAnchor } from '@/features/marquee/hooks/use-mobile-search-anchor'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import {
	attendeesQueryOptions,
	eventQueryOptions,
	operatorQueryOptions
} from '@/features/marquee/query-options'
import {
	attendeeMatchesFilters,
	dataSourceToLabelKey,
	filterAttendees,
	hasNoSalesforceAccount,
	incompleteAccountRows,
	invalidStateMessage,
	isMarketManagerAllowed,
	manualWorkTally,
	type RsvpFilterId,
	rsvpClosedByLockCount,
	rsvpFilterOptions,
	zoneAbbr
} from '@/features/marquee/utils'
import { errorHandler } from '@/helpers/utils'
import { useMarqueeEventDetailContext } from '@/layouts/marquee-event-detail-layout'
import { routes } from '@/routes'
import {
	importInvitees,
	type MarqueeAttendee,
	type MarqueeCompletenessWarning,
	type MarqueeEventState,
	type MarqueeOperator,
	restoreResolution
} from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/** Long enough to read past the dialog closing and the page settling. */
const LOCK_WARNING_TOAST_DURATION_MS = 10_000

/**
 * Which setup controls this roster shows, answered on both axes at once: the
 * lifecycle state that allows the write, and the role that may perform it
 * (FR51). Neither answers the other, and a screen that asked only one of them
 * would either hide a creator's button or show a facilitator one that 403s.
 *
 * The names say "show", not "can", because that is what they decide: the whole
 * answer for whether the control is rendered, with nothing left to combine at
 * the call site.
 *
 * Setup is creator work throughout: a facilitator reads this roster and fixes
 * its rows (assign host, retag, mark unresolvable, all open to both roles) but
 * never edits the event, replaces the CSV, hand-adds an invitee or moves the
 * lifecycle on.
 */
const visibleSetupActions = (
	state: MarqueeEventState | undefined,
	operator: MarqueeOperator | undefined
) => {
	const isPreWork = state === 'pre_work'
	const showEditButton =
		isPreWork && isMarketManagerAllowed(operator, 'updateEvent')
	// The one control here with no state axis at all: the backend accepts a
	// delete in every state, the closed and already-exported ones included, so
	// role is the whole answer.
	const showDeleteButton = isMarketManagerAllowed(operator, 'deleteEvent')
	return {
		showEditButton,
		showDeleteButton,
		showHeaderActions:
			state !== undefined && (showEditButton || showDeleteButton),
		showReplaceCsv:
			isPreWork && isMarketManagerAllowed(operator, 'importInvitees'),
		showAddAttendee:
			isPreWork && isMarketManagerAllowed(operator, 'addAttendee'),
		showLockBanner:
			isPreWork && isMarketManagerAllowed(operator, 'lockEvent'),
		showStartButton:
			state === 'locked' && isMarketManagerAllowed(operator, 'startEvent')
	}
}

/**
 * The two event-level actions in the detail header. Edit is pre_work-only and
 * creator-only, so it disappears once the event is locked and never appears for
 * a facilitator; delete is creator-only with no state to wait for, so on a
 * locked event it is the only one left and takes the row on its own.
 *
 * Side by side at every width: on mobile the two share the row rather than
 * stacking, which would push the roster down by a button's height for a pair of
 * actions used once each.
 */
function SetupHeaderActions({
	eventId,
	eventName,
	showEdit,
	showDelete,
	onDeleted
}: {
	eventId: string
	eventName?: string
	showEdit: boolean
	showDelete: boolean
	onDeleted: () => void
}) {
	const { t } = useTranslation()
	return (
		<div className='flex l:w-auto w-full items-center gap-2'>
			{showDelete && (
				<DeleteEventDialog
					eventId={eventId}
					eventName={eventName}
					onDeleted={onDeleted}
				>
					<ResponsiveConfirmTrigger>
						{/* Ghost: the quieter half of the pair, so Edit keeps
						    the only outline. */}
						<Button
							className='flex-1 l:flex-none'
							size='sm'
							type='button'
							variant='tertiary'
						>
							{t('marquee:events_delete')}
						</Button>
					</ResponsiveConfirmTrigger>
				</DeleteEventDialog>
			)}
			{showEdit && (
				<Button
					asChild
					className='flex-1 l:flex-none'
					size='sm'
					variant='secondaryOutline'
				>
					<Link
						to={generatePath(
							routes.MARQUEE_EVENT_EDIT.handle.fullPath,
							{ id: eventId }
						)}
					>
						{t('marquee:edit_event')}
					</Link>
				</Button>
			)}
		</div>
	)
}

function MarqueeEventAttendees() {
	const { t } = useTranslation()
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { setEndSlot, stickyHeaderRef } = useMarqueeEventDetailContext()
	const csvInputRef = useRef<HTMLInputElement>(null)
	// On mobile the search collapses into an icon beside the list title; opening
	// it hides the title so the input can take the whole row (as in check-in).
	const isDesktop = useMinWidth('l')
	const [query, setQuery] = useState('')
	const [isSearchOpen, setIsSearchOpen] = useState(false)
	const showMobileSearch = !isDesktop && isSearchOpen
	// "Replace CSV" confirms first, then opens the file picker.
	const [isReplaceOpen, setIsReplaceOpen] = useState(false)
	// Same multi-value facet the check-in desk carries, on the same field: an
	// empty selection is "any answer", and picking two of them keeps both lanes.
	// A single choice made the common reads ("who has not answered or declined")
	// two passes over the same list.
	const [rsvpFilter, setRsvpFilter] = useState<RsvpFilterId[]>([])
	// Account completeness is a second, independent facet: it narrows the list on
	// top of the RSVP answer rather than instead of it (a declined row can be
	// missing an account too), so it is a switch beside the facet and never one of
	// its values. The completeness banner turns it on — it is the fix surface for
	// what the banner warns about.
	const [onlyIncompleteAccounts, setOnlyIncompleteAccounts] = useState(false)
	// The row being resolved, if any — the assign-host dialog is open while set.
	const [assignHostTarget, setAssignHostTarget] =
		useState<AssignHostTarget | null>(null)
	const [unresolvableTarget, setUnresolvableTarget] =
		useState<UnresolvableTarget | null>(null)

	const { data: event } = useQuery(eventQueryOptions(id))
	const {
		data: attendees,
		isLoading,
		isError
	} = useQuery(attendeesQueryOptions(id))

	// Lifecycle is fully manual: pre_work → locked → live → closed. Editing and
	// CSV replace are pre_work-only; locking then arms the "Start event" action
	// (locked → live) that opens day-of check-in. What the operator is allowed
	// to do with any of it is the other axis; both are folded in by
	// `visibleSetupActions`.
	const { data: operator } = useQuery(operatorQueryOptions())
	const {
		showEditButton,
		showReplaceCsv,
		showAddAttendee,
		showLockBanner,
		showStartButton,
		showDeleteButton,
		showHeaderActions
	} = visibleSetupActions(event?.state, operator)

	// "Replace CSV": re-import the invitee list for this event (re-runs dedupe +
	// resolve on the backend), then the mutation invalidates the attendee query.
	const queryClient = useQueryClient()
	const replaceCsv = useMutation({
		mutationFn: async (file: File) => {
			const response = await importInvitees({
				path: { eventId: id ?? '' },
				body: { file }
			})
			return response.data
		},
		onSuccess: async (result) => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(id ?? '')
			})
			showImportSuccessToast(result)
		},
		onError: (error) => handleImportError(error)
	})
	/**
	 * Walks back a give-up verdict from the roster.
	 *
	 * The mark is an operator's judgement, not a fact the system established, so
	 * every surface that can make it has to be able to undo it. The desk and the
	 * export tab already could, and this roster is where the lock's completeness
	 * warning sends the operator, so it is the likeliest place to change one's
	 * mind. No confirmation: the mark is what needed confirming, and this puts
	 * the row back among the ones still to resolve.
	 */
	const [restoringAttendeeId, setRestoringAttendeeId] = useState<
		string | null
	>(null)
	const restore = useMutation({
		mutationFn: ({ attendee }: { attendee: MarqueeAttendee }) =>
			restoreResolution({ path: { attendeeId: attendee.id } }),
		onSuccess: async (_data, { attendee }) => {
			// Same event prefix the mark uses: the roster, the lock's warning and
			// the export gate all move when a row comes back into play.
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(id ?? '')
			})
			toast.success(
				t('marquee:checkin_toast_restored', { name: attendee.name })
			)
		},
		onError: (error) => {
			// `restore-resolution` shares the 409 `invalid-state` code with seven
			// other writes, so the backend's sentence names no action. Only
			// `locked` refuses this one, which is what the message says.
			const refusal = invalidStateMessage({
				error,
				action: 'restore_resolution',
				eventState: event?.state
			})
			if (refusal) {
				toast.error(
					t(refusal.key, {
						state: refusal.stateKey ? t(refusal.stateKey) : ''
					})
				)
				return
			}
			errorHandler(error)
		},
		onSettled: () => setRestoringAttendeeId(null)
	})

	// The confirmation comes first, so by the time the picker returns a file the
	// operator has already agreed to replace the roster.
	const openCsvPicker = () => {
		setIsReplaceOpen(false)
		csvInputRef.current?.click()
	}
	const handleReplaceCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		e.target.value = '' // allow picking the same file again
		if (file && id) {
			replaceCsv.mutate(file)
		}
	}

	useEffect(() => {
		setEndSlot(
			showHeaderActions ? (
				<SetupHeaderActions
					eventId={id ?? ''}
					eventName={event?.name}
					onDeleted={() =>
						navigate(routes.MARQUEE_EVENTS.handle.fullPath)
					}
					showDelete={showDeleteButton}
					showEdit={showEditButton}
				/>
			) : null
		)
		return () => setEndSlot(null)
	}, [
		setEndSlot,
		id,
		event?.name,
		navigate,
		showEditButton,
		showDeleteButton,
		showHeaderActions
	])

	// The roster is a flat list; search filters it client-side (nothing here is
	// paginated, so the whole list is already in the cache).
	const rows = useMemo(() => attendees?.rows ?? [], [attendees?.rows])
	const filteredRows = useMemo(
		() =>
			filterAttendees(rows, query)
				.filter((row) =>
					attendeeMatchesFilters(row, {
						rsvp: rsvpFilter,
						checkIn: []
					})
				)
				.filter(
					(row) =>
						!onlyIncompleteAccounts || hasNoSalesforceAccount(row)
				),
		[rows, query, rsvpFilter, onlyIncompleteAccounts]
	)
	// Stated in the lock confirmation: locking closes these rows as "no response"
	// for good, so the operator sees the number before committing.
	const closingRsvpCount = useMemo(() => rsvpClosedByLockCount(rows), [rows])
	// Non-walk-in rows with no Salesforce account at all: the backend's M8 gate
	// warns about these on lock, and they are what silently drops out of the CSV.
	// Counted here rather than read off the lock response, which carries the
	// number once and loses it on the next refresh.
	const incompleteAccountCount = useMemo(
		() => incompleteAccountRows(rows),
		[rows]
	)

	// The lock's own answer, at the moment of the transition: the AC's trigger is
	// the response, not the roster. Announced as a toast because the banner below
	// simply appears in the page flow, which is not loud at the second the
	// operator is looking at the button they just pressed; the banner is what the
	// warning persists as once the toast is gone.
	const handleLocked = (warning: MarqueeCompletenessWarning | null) => {
		const incompleteRows = warning?.incomplete_rows ?? 0
		if (incompleteRows > 0) {
			// Neutral rather than `toast.error`: the lock succeeded, and this is
			// what to fix next rather than a failure to acknowledge.
			toast(
				t('marquee:incomplete_accounts_toast', {
					count: incompleteRows
				}),
				{ duration: LOCK_WARNING_TOAST_DURATION_MS }
			)
		}
	}

	// Keep the page from jumping while the mobile search filters the list.
	const { cardRef, spacerRef, bottomSpacer, onSearchFocus, onSearchBlur } =
		useMobileSearchAnchor({
			active: showMobileSearch,
			stickyHeaderRef,
			measureKey: filteredRows
		})

	// Clears the other two narrowings on the way in, so the roster shows the rows
	// the banner counted and not their intersection with whatever pill or search
	// happened to be active — a smaller list there reads as "the rest are already
	// fixed", which is the silent failure this warning exists to prevent.
	const handleReviewIncomplete = () => {
		setQuery('')
		// Collapse the mobile search along with the term it held: left open it
		// covers the list title with an empty field, and the list narrows from a
		// switch the operator cannot see from there.
		setIsSearchOpen(false)
		setRsvpFilter([])
		setOnlyIncompleteAccounts(true)
		// Below the breakpoint the table sits off-screen under the banner, so
		// filtering it alone looks like nothing happened. Deferred a frame so the
		// scroll targets the card as it is once the narrowed list has rendered.
		requestAnimationFrame(() => {
			cardRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start'
			})
		})
	}
	// Stated in the replace-CSV confirmation, for the same reason: a re-import
	// replaces every attendee, and only rows the new file matches on email keep
	// their manual work. What survives is unknowable until the server has seen
	// the file, so the dialog quantifies what is at stake instead.
	const manualWork = useMemo(() => manualWorkTally(rows), [rows])

	// The import instant is rendered in the event's zone, not the viewer's, so it
	// carries the zone abbreviation: without it the line reads as local time to a
	// reader sitting in another country, which is the wrong hour.
	const lastRefreshed = (() => {
		const at = formatDate(
			attendees?.last_refreshed_at,
			'lll',
			event?.time_zone
		)
		if (at === '-') {
			return at
		}
		const abbr = zoneAbbr(attendees?.last_refreshed_at, event?.time_zone)
		return abbr ? `${at} ${abbr}` : at
	})()
	const dataSourceKey = attendees?.data_source
		? dataSourceToLabelKey[attendees.data_source]
		: undefined
	return (
		<div className='flex flex-col gap-6 l:px-10 px-4 pt-6 pb-10'>
			<title>{t('marquee:page_title_attendees')}</title>
			<AttendeeSummary
				counts={event?.counts}
				invited={event?.invited}
				isLoading={!event}
				rows={rows}
				state={event?.state}
			/>

			{/* The roster's completeness warning: these rows reach no Salesforce
			    account, so they will not export. Shown in every state, pre_work
			    included, because the roster rule is the same before and after
			    the lock and the fix is cheapest before it; it stays until the
			    accounts are filled in. The check-in desk draws the same one. */}
			{event && (
				<IncompleteAccountsBanner
					count={incompleteAccountCount}
					onReview={handleReviewIncomplete}
				/>
			)}

			{/* Pre-work prompt: lock the invitee list and move on to check-in.
			    Creator work, so a facilitator sees the roster without it. */}
			{showLockBanner && (
				<InlineBanner
					action={
						<LockEventDialog
							closingRsvpCount={closingRsvpCount}
							eventId={id}
							onLocked={handleLocked}
							trigger={
								<Button
									className='l:w-auto w-full'
									disabled={isLoading}
									type='button'
									variant='primary'
								>
									{t('marquee:lock_and_continue')}
								</Button>
							}
						/>
					}
					description={t('marquee:lock_banner_description')}
					title={t('marquee:lock_banner_title')}
				/>
			)}

			{/* Toolbar: search (desktop) on the left, the setup actions and the
			    lifecycle CTA anchored right — the same shape the check-in desk
			    draws, so "Start event" sits in one place across the two tabs.
			    Hand-add and Replace CSV are pre_work-only and creator-only, and
			    Start is locked-only, so the group is never more than two buttons
			    and is empty altogether for a facilitator. */}
			<div className='flex l:flex-row flex-col l:items-center l:justify-between gap-3'>
				{isDesktop && (
					<SearchInput
						className='l:w-[345px]'
						clearLabel={t('common:clear_search')}
						onChange={setQuery}
						placeholder={t('marquee:attendee_search_placeholder')}
						value={query}
					/>
				)}
				<div className='flex l:flex-row flex-col gap-3 l:gap-4'>
					{showAddAttendee && (
						<AddAttendeeDialog
							eventId={id}
							trigger={
								<Button
									className='l:w-auto w-full'
									disabled={isLoading}
									type='button'
									variant='secondary'
								>
									<PlusIcon />
									{t('marquee:add_attendee')}
								</Button>
							}
						/>
					)}
					{showReplaceCsv && (
						<>
							<input
								accept='.csv,text/csv'
								className='hidden'
								onChange={handleReplaceCsv}
								ref={csvInputRef}
								type='file'
							/>
							<ResponsiveConfirmDialog
								confirmLabel={t(
									'marquee:replace_csv_dialog_confirm'
								)}
								description={
									<ReplaceCsvDescription
										manualWork={manualWork}
									/>
								}
								onConfirm={openCsvPicker}
								onOpenChange={setIsReplaceOpen}
								open={isReplaceOpen}
								title={t('marquee:replace_csv_dialog_title')}
							>
								<ResponsiveConfirmTrigger>
									<Button
										className='l:w-auto w-full'
										disabled={
											isLoading || replaceCsv.isPending
										}
										type='button'
										variant='secondaryOutline'
									>
										{t('marquee:replace_csv')}
										{replaceCsv.isPending && (
											<Spinner size={16} />
										)}
									</Button>
								</ResponsiveConfirmTrigger>
							</ResponsiveConfirmDialog>
						</>
					)}
					{/* Once locked, Start (locked → live) opens day-of check-in,
					    for the creator who owns the lifecycle. */}
					{showStartButton && (
						<StartEventDialog
							eventId={id}
							trigger={
								<Button
									className='l:w-auto w-full'
									disabled={isLoading}
									type='button'
									variant='primary'
								>
									{t('marquee:start_event')}
								</Button>
							}
						/>
					)}
				</div>
			</div>

			<MarqueeTableCard
				header={
					<div className='flex l:flex-row flex-col l:items-center l:justify-between gap-4'>
						<div className='flex l:w-auto w-full items-center gap-2'>
							{!showMobileSearch && (
								<h2 className='min-w-0 flex-1 font-semibold text-lg text-neutral-900'>
									{t('marquee:attendee_list_title')}
								</h2>
							)}
							{showMobileSearch && (
								<SearchInput
									autoFocus
									className='flex-1'
									clearLabel={t('common:clear_search')}
									onBlur={onSearchBlur}
									onChange={setQuery}
									onDismiss={() => setIsSearchOpen(false)}
									onFocus={onSearchFocus}
									placeholder={t(
										'marquee:attendee_search_placeholder'
									)}
									value={query}
								/>
							)}
							{!(isDesktop || showMobileSearch) && (
								<Button
									aria-label={t(
										'marquee:attendee_search_placeholder'
									)}
									onClick={() => setIsSearchOpen(true)}
									shape='square'
									size='md'
									type='button'
									variant='secondaryOutline'
								>
									<SearchLgIcon />
								</Button>
							)}
						</div>
						{/* Wraps below the breakpoint, where the header is already
						    a column and the facets sit under the title — the same
						    row the check-in desk draws. */}
						<div className='flex flex-wrap items-center gap-4'>
							<RosterFilterSelect
								label='marquee:roster_filter_rsvp_status'
								onChange={setRsvpFilter}
								options={rsvpFilterOptions}
								value={rsvpFilter}
							/>
							{/* Its own switch rather than a value of the RSVP
							    facet: the two are orthogonal and narrow the list
							    together, so a row can be declined and accountless
							    at once. Offered whenever the warning that turns it
							    on exists, in any state. Stays put while it is on
							    even once the count reaches zero: fixing the last
							    row is exactly what the filter leads to, and
							    withdrawing the control there would strand the
							    operator on an empty list with no way to switch it
							    off. */}
							{(incompleteAccountCount > 0 ||
								onlyIncompleteAccounts) && (
								<Switch
									checked={onlyIncompleteAccounts}
									label={t(
										'marquee:attendee_filter_no_account'
									)}
									onChange={setOnlyIncompleteAccounts}
								/>
							)}
						</div>
					</div>
				}
				ref={cardRef}
			>
				<AttendeesTable
					captureMode={event?.capture_mode}
					data={filteredRows}
					eventId={id}
					eventState={event?.state}
					isError={isError}
					isLoading={isLoading}
					onAssignHost={(attendee) =>
						setAssignHostTarget({
							attendeeId: attendee.id,
							name: attendee.name
						})
					}
					onMarkUnresolvable={(attendee) =>
						setUnresolvableTarget({
							attendeeId: attendee.id,
							name: attendee.name,
							hostId: attendee.airbnb_host_id ?? undefined
						})
					}
					onRestoreResolution={(attendee) => {
						setRestoringAttendeeId(attendee.id)
						restore.mutate({ attendee })
					}}
					restoringAttendeeId={restoringAttendeeId}
				/>
			</MarqueeTableCard>

			{/* The same dialog the check-in and export tabs open, so a row fixed
			    from the roster is fixed everywhere. Marking a row unresolvable is
			    deliberately not offered here: that verdict belongs to export prep,
			    and this surface exists to resolve rows rather than give up on
			    them. */}
			<AssignHostDialog
				eventId={id}
				eventState={event?.state}
				onClose={() => setAssignHostTarget(null)}
				onMarkUnresolvable={(handover) =>
					setUnresolvableTarget({
						attendeeId: handover.attendeeId,
						name: handover.name
					})
				}
				target={assignHostTarget}
			/>

			<MarkUnresolvableDialog
				eventId={id}
				eventState={event?.state}
				onClose={() => setUnresolvableTarget(null)}
				onMarked={() => setAssignHostTarget(null)}
				target={unresolvableTarget}
			/>

			{/* Data-source / freshness bar — inline at the bottom of the content.
			    Stacked and flush at every width: the two lines are one fact
			    about the same import, so they read as a block rather than as
			    two ends of a row. */}
			<div className='flex flex-col border-neutral-200 border-t pt-4 text-neutral-500 text-sm'>
				<p>
					<span className='font-bold text-neutral-700'>
						{t('marquee:data_source_label')}
					</span>{' '}
					{dataSourceKey
						? t(dataSourceKey)
						: (attendees?.data_source ?? '-')}
				</p>
				<p>
					<span className='font-bold text-neutral-700'>
						{t('marquee:imported_label')}
					</span>{' '}
					{lastRefreshed}
				</p>
			</div>

			{/* Holds the page height steady while the mobile search filters the
			    list, so the body scroll doesn't jump. Sits below everything, so
			    the reserved space is off-screen. */}
			{bottomSpacer > 0 && (
				<div
					aria-hidden='true'
					ref={spacerRef}
					style={{ height: bottomSpacer }}
				/>
			)}
		</div>
	)
}

/**
 * The replace-CSV confirmation body: what a re-import does, and how much hand
 * reconciliation is on the roster right now.
 *
 * The tally renders only when there is manual work at stake, so a freshly
 * imported roster keeps the plain one-line warning. The categories are listed
 * without a headline total: a row can be in more than one of them, so any such
 * total would read as a sum that does not add up.
 */
function ReplaceCsvDescription({
	manualWork
}: {
	manualWork: ReturnType<typeof manualWorkTally>
}) {
	const { t } = useTranslation()
	const items = [
		manualWork.pins > 0 &&
			t('marquee:replace_csv_dialog_manual_pins', {
				count: manualWork.pins
			}),
		manualWork.marks > 0 &&
			t('marquee:replace_csv_dialog_manual_marks', {
				count: manualWork.marks
			}),
		manualWork.handAdded > 0 &&
			t('marquee:replace_csv_dialog_manual_hand_added', {
				count: manualWork.handAdded
			})
	].filter((item) => item !== false)

	return (
		<span className='flex flex-col gap-2'>
			<span>{t('marquee:replace_csv_dialog_description')}</span>
			{items.length > 0 && (
				<span className='flex flex-col items-start gap-0.5 font-medium text-neutral-900'>
					{items.map((item) => (
						<span key={item}>{item}</span>
					))}
				</span>
			)}
		</span>
	)
}

export { MarqueeEventAttendees }
